<script setup lang="ts">
import { computed } from 'vue'
import { IconCircleCheckFilled, IconCircle } from '@tabler/icons-vue'

type BubbleMessage = {
  id: number
  author: string
  time: string
  text: string
  mine?: boolean
}

const props = defineProps<{
  message: BubbleMessage
  images: Array<{ src: string; imageIndex: number }>
  showText: boolean
  selectable?: boolean
  messageSelected?: boolean
  selectedImageIndexes?: number[]
  avatarImage?: string | null
  avatarInitials?: string
  avatarClass?: string
}>()

const emit = defineEmits<{
  (e: 'toggle-message', messageId: number): void
  (e: 'toggle-image', payload: { messageId: number; imageIndex: number }): void
}>()

const isSelectable = computed(() => Boolean(props.selectable))
const isMessageSelected = computed(() => Boolean(props.messageSelected))
const selectedImageIndexes = computed(() => props.selectedImageIndexes ?? [])

function onToggleMessage() {
  if (!isSelectable.value) return
  emit('toggle-message', props.message.id)
}

function onToggleImage(imageIndex: number) {
  if (!isSelectable.value) return
  emit('toggle-image', {
    messageId: props.message.id,
    imageIndex,
  })
}

function isImageSelected(imageIndex: number) {
  return selectedImageIndexes.value.includes(imageIndex)
}
</script>

<template>
  <div class="flex items-end gap-2" :class="message.mine ? 'justify-end' : 'justify-start'">
    <button
      v-if="isSelectable && !message.mine"
      type="button"
      class="self-center text-primary"
      @click="onToggleMessage"
    >
      <IconCircleCheckFilled v-if="isMessageSelected" size="20" />
      <IconCircle v-else size="20" />
    </button>

    <div
      v-if="!message.mine"
      class="h-7 w-7 shrink-0 rounded-full border border-base-300 overflow-hidden flex items-center justify-center text-[10px] font-semibold"
      :class="avatarClass"
    >
      <img
        v-if="avatarImage"
        :src="avatarImage"
        :alt="`${message.author} avatar`"
        class="h-full w-full object-cover"
      />
      <span v-else>{{ avatarInitials }}</span>
    </div>

    <div class="max-w-[80%]">
      <div
        class="px-3 py-2 shadow-sm"
        :class="
          message.mine
            ? 'bg-[#55cfc8] text-white rounded-2xl rounded-br-md'
            : 'bg-base-100 text-base-content rounded-2xl rounded-bl-md'
        "
      >
        <div v-if="images.length" class="grid grid-cols-3 gap-1 mb-2">
          <button
            v-for="image in images"
            :key="`${message.id}-${image.imageIndex}`"
            type="button"
            class="relative"
            @click.stop="onToggleImage(image.imageIndex)"
          >
            <img
              :src="image.src"
              :alt="`Event memory from ${message.author}`"
              class="h-16 w-full rounded-md object-cover"
            />
            <span
              v-if="isSelectable"
              class="absolute right-1 top-1 text-primary bg-base-100 rounded-full"
            >
              <IconCircleCheckFilled v-if="isImageSelected(image.imageIndex)" size="18" />
              <IconCircle v-else size="18" />
            </span>
          </button>
        </div>
        <p v-if="showText" class="text-sm leading-5">{{ message.text }}</p>
      </div>
      <div
        class="px-1 pt-1 text-[11px] text-base-content/45"
        :class="message.mine ? 'text-right' : 'text-left'"
      >
        {{ message.time }}
      </div>
    </div>

    <div
      v-if="message.mine"
      class="h-7 w-7 shrink-0 rounded-full border border-base-300 overflow-hidden flex items-center justify-center text-[10px] font-semibold"
      :class="avatarClass"
    >
      <img
        v-if="avatarImage"
        :src="avatarImage"
        :alt="`${message.author} avatar`"
        class="h-full w-full object-cover"
      />
      <span v-else>{{ avatarInitials }}</span>
    </div>

    <button
      v-if="isSelectable && message.mine"
      type="button"
      class="self-center text-primary"
      @click="onToggleMessage"
    >
      <IconCircleCheckFilled v-if="isMessageSelected" size="20" />
      <IconCircle v-else size="20" />
    </button>
  </div>
</template>
